import {
  CommonNotificationType,
  IDiscountCode,
  INotificationBaseMessage,
  INotificationBaseRelatedToShow,
  NotificationType,
} from "@evennt-tech/api-v2-core";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { shallow } from "zustand/shallow";
import { apiV2 } from "api/firebase";
import {
  hasDiscountCodeAmountChanged,
  parseResponseDiscountCode,
  validateDiscountCodeCategories,
} from "tools/discountCode";
import { IPayloadUpdateDiscountCodes } from "@evennt-tech/api-v2-core/dist/notification/payload/discount-code";
import { DiscountCodeError } from "view/apps/Exhibitor/pages/Cart/components/DiscountCodes/helpers";
import { v4 as uuid } from "uuid";
import { DISCOUNT_AMOUNT_UPDATED_MESSAGE } from "view/apps/Exhibitor/pages/Cart/components/DiscountCodes/constants";

type Snackbar = { id: string; message: string };
export type CartDiscountCode = IDiscountCode & {
  error?: DiscountCodeError;
  remainingAmount?: number;
};
type State = {
  itemsByShow: Map<string, CartDiscountCode[]>;
  snackbars: Snackbar[];
  remainingAmount: number;
};

type Action = {
  append: (showId: string, discountCode: IDiscountCode) => void;
  removeOne: (showId: string, discountCodeId: string) => void;
  reset: (showId: string) => void;
  validate: (
    showId: string,
    standId: string,
    categoryIdsBySupplier: Record<string, Set<string>>
  ) => Promise<boolean>;
  fetch: (codeId: string, showId: string) => Promise<void>;
  autoCatch: (showId: string) => () => void;
  updateRemainingAmount: (remainingAmount: number) => void;
  hideSnackbar: (id: string) => void;
  enqueueSnackbar: (message: string, hideAfter?: number) => void;
  updateError: (
    showId: string,
    categoryIdsBySupplier: Record<string, Set<string>>
  ) => void;
};

type Store = Action & State;

export const discountCodeStore = create(
  devtools<Store>(
    (set, get) => ({
      itemsByShow: new Map(),
      snackbars: [],
      remainingAmount: 0,
      append: (showId, discountCode) => {
        const { itemsByShow } = get();
        const newItemsByShow = new Map(itemsByShow);
        const showDiscountCodes = newItemsByShow.get(showId);

        if (!showDiscountCodes) {
          newItemsByShow.set(showId, [discountCode]);
        } else {
          const newShowDiscountCodes = [...showDiscountCodes];
          if (!showDiscountCodes.some((item) => item.id === discountCode.id)) {
            newShowDiscountCodes.push(discountCode);
          }

          newItemsByShow.set(showId, newShowDiscountCodes);
        }
        set({ itemsByShow: newItemsByShow });
      },
      removeOne: (showId, discountCodeId) => {
        const newItemsByShow = new Map(get().itemsByShow);
        const showDiscountCodes = newItemsByShow.get(showId);
        if (!showDiscountCodes) {
          return;
        }

        newItemsByShow.set(
          showId,
          showDiscountCodes.filter((code) => code.id !== discountCodeId)
        );
        set({ itemsByShow: newItemsByShow });
      },
      reset: (showId) => {
        const { itemsByShow } = get();
        const newItemsByShow = new Map(itemsByShow);
        newItemsByShow.delete(showId);
        set({ itemsByShow: newItemsByShow, snackbars: [], remainingAmount: 0 });
      },
      validate: async (showId, standId, categoryIdsBySupplier) => {
        const { itemsByShow } = get();
        const showDiscountCodes = itemsByShow.get(showId);
        if (!showDiscountCodes?.length) {
          return true;
        }

        try {
          const errorByDiscountCode = await apiV2.discountCode.validateMany({
            showId,
            standId,
            codeIds: showDiscountCodes.map(({ id }) => id),
          });
          const newShowDiscountCodes = showDiscountCodes.map((code) => {
            const result = errorByDiscountCode[code.id];
            if (!result) {
              return { ...code, error: DiscountCodeError.Expired };
            }

            const error =
              errorByDiscountCode[code.id]?.error ??
              validateDiscountCodeCategories(
                result.categories,
                code.supplierCompanyId,
                categoryIdsBySupplier
              );
            return {
              ...code,
              ...(error !== null && { error: error as DiscountCodeError }),
            };
          });

          if (!newShowDiscountCodes.some((c) => c.error)) {
            return true;
          }

          const newItemsByShow = new Map(itemsByShow);
          newItemsByShow.set(showId, newShowDiscountCodes);
          set({ itemsByShow: newItemsByShow });
          return false;
        } catch (e) {
          console.error("Error occurred validating discount codes: ", e);
          return false;
        }
      },
      fetch: async (codeId, showId) => {
        try {
          const { itemsByShow, snackbars } = get();
          const showDiscountCodes = itemsByShow.get(showId) ?? [];
          const appliedDiscountCode = showDiscountCodes.find(
            (code) => code.id === codeId
          );

          if (!appliedDiscountCode) {
            return;
          }
          const updatedCode = await apiV2.discountCode.getOne({
            id: codeId,
          });
          const parsedDiscountCode = parseResponseDiscountCode(updatedCode);
          const hasAmountChanged = hasDiscountCodeAmountChanged(
            appliedDiscountCode,
            parsedDiscountCode
          );
          if (!hasAmountChanged) {
            return;
          }

          const newItemsByShow = new Map(itemsByShow);
          newItemsByShow.set(
            showId,
            showDiscountCodes.map((code) =>
              code.id === codeId
                ? { ...code, rules: parsedDiscountCode.rules }
                : code
            )
          );
          const snackbarId = uuid();
          const newSnackbars: Snackbar[] = [
            ...snackbars,
            { id: snackbarId, message: DISCOUNT_AMOUNT_UPDATED_MESSAGE },
          ];

          set({ itemsByShow: newItemsByShow, snackbars: newSnackbars });
        } catch (e) {
          console.error("Error occurred fetching discount code: ", e);
        }
      },

      autoCatch: (showId: string) => {
        const { fetch: fetchDiscountCode } = get();

        apiV2.notification.subscribe<
          INotificationBaseRelatedToShow<IPayloadUpdateDiscountCodes>
        >({
          key: NotificationType.UPDATE_DISCOUNT_CODES,
          showId,
          callback: async ({ payload }) => {
            if (!payload?.codeId) {
              return;
            }

            await fetchDiscountCode(payload.codeId, showId);
          },
        });

        apiV2.notification.subscribe<
          INotificationBaseMessage<IPayloadUpdateDiscountCodes>
        >({
          key: CommonNotificationType.UPDATE_DISCOUNT_CODES,
          callback: async ({ payload }) => {
            if (!payload?.codeId) {
              return;
            }

            await fetchDiscountCode(payload.codeId, showId);
          },
        });

        return () => {
          apiV2.notification.unsubscribe({
            key: NotificationType.UPDATE_DISCOUNT_CODES,
            showId,
          });
          apiV2.notification.unsubscribe({
            key: CommonNotificationType.UPDATE_DISCOUNT_CODES,
          });
        };
      },
      updateRemainingAmount: (newRemainingAmount) => {
        set({ remainingAmount: newRemainingAmount });
      },
      hideSnackbar: (id) => {
        const { snackbars } = get();
        const newSnackbars = snackbars.filter((s) => s.id !== id);
        set({ snackbars: newSnackbars });
      },
      enqueueSnackbar: (message) => {
        const id = uuid();
        const { snackbars } = get();
        const newSnackbars: Snackbar[] = [...snackbars, { id, message }];
        set({ snackbars: newSnackbars });
      },
      updateError: (
        showId: string,
        categoryIdsBySupplier: Record<string, Set<string>>
      ) => {
        const { itemsByShow } = get();
        const appliedDiscountCodes = itemsByShow.get(showId);
        if (!appliedDiscountCodes?.length) {
          return;
        }
        const shouldUpdate = appliedDiscountCodes.some(
          (c) =>
            c.error &&
            [
              DiscountCodeError.NoMatchingProducts,
              DiscountCodeError.CategoryMismatch,
            ].includes(c.error)
        );
        if (!shouldUpdate) {
          return;
        }
        const newDiscountCodes = appliedDiscountCodes.map((c) => {
          if (c.error === DiscountCodeError.NoMatchingProducts) {
            const isValid = Object.keys(categoryIdsBySupplier).includes(
              c.supplierCompanyId
            );
            return {
              ...c,
              error: isValid ? undefined : DiscountCodeError.NoMatchingProducts,
            };
          }
          if (c.error === DiscountCodeError.CategoryMismatch) {
            const isValid =
              !c.productCategories?.length ||
              c.productCategories?.some((cat) =>
                Object.values(categoryIdsBySupplier).some((categories) =>
                  categories.has(cat.productCategoryId)
                )
              );
            return {
              ...c,
              error: isValid ? undefined : DiscountCodeError.CategoryMismatch,
            };
          }

          return c;
        });
        const newItemsByShow = new Map(itemsByShow);
        newItemsByShow.set(showId, newDiscountCodes);
        set({ itemsByShow: newItemsByShow });
      },
    }),
    { name: "discount-codes" }
  )
);

export const useShowDiscountCodes = (showId: string) =>
  discountCodeStore(
    ({ itemsByShow }) => itemsByShow.get(showId) ?? [],
    shallow
  );
export const useShowDiscountCodesActions = () =>
  discountCodeStore(
    ({
      append,
      removeOne,
      reset,
      validate,
      autoCatch,
      enqueueSnackbar,
      hideSnackbar,
      updateError,
    }) => ({
      append,
      removeOne,
      reset,
      validate,
      autoCatch,
      enqueueSnackbar,
      hideSnackbar,
      updateError,
    })
  );
export const useIsShowDiscountCodesInvalid = (showId: string): boolean =>
  discountCodeStore(
    ({ itemsByShow }) =>
      !!itemsByShow.get(showId)?.some((c) => c.error, shallow)
  );
export const useDiscountCodeSnackbar = () =>
  discountCodeStore(({ snackbars }) => Array.from(snackbars)[0], shallow);
export const useDiscountCodeRemainingAmount = () =>
  discountCodeStore(({ remainingAmount }) => remainingAmount, shallow);
